import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
}

function FAQItem({ question, answer }: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-border pb-4 mb-4 last:mb-0 last:border-0">
      <button 
        className="flex w-full justify-between items-center text-left font-medium text-lg py-2"
        onClick={() => setIsOpen(!isOpen)}
      >
        {question}
        <span className="flex-shrink-0 ml-2">
          {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </span>
      </button>
      {isOpen && (
        <div className="mt-2 text-muted-foreground">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="min-h-screen bg-background py-10">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <Link href="/">
            <Button variant="ghost" className="flex items-center gap-1 hover:bg-transparent hover:text-primary p-0">
              <ChevronLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Button>
          </Link>
        </div>
        
        <div className="max-w-3xl mx-auto bg-card p-8 rounded-lg shadow-sm">
          <h1 className="text-3xl font-bold mb-6">Frequently Asked Questions</h1>
          
          <div className="prose dark:prose-invert max-w-none">
            <p className="mb-8">
              Find answers to common questions about NoLicense Nearby's music licensing service.
            </p>
            
            <div className="space-y-2">
              <FAQItem 
                question="How soon can I use my licensed music?" 
                answer={
                  <p>
                    Your license is active immediately upon successful payment. You can start using your licensed music right away.
                    Your official licensing agreement documentation will be emailed to you within 48 hours of transaction completion.
                  </p>
                } 
              />
              
              <FAQItem 
                question="What license type should I choose?" 
                answer={
                  <div>
                    <p className="mb-4">Here's a quick guide on which license to choose:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Basic Creator License:</strong> For personal YouTube videos, small creators, and non-monetized content</li>
                      <li><strong>Professional License:</strong> For monetized content across platforms, small business use, and perpetual usage</li>
                      <li><strong>Enterprise License:</strong> For commercial broadcasts, major brands, and unlimited audience reach</li>
                    </ul>
                    <p className="mt-4">If you're still unsure, you can request a custom license tailored to your specific needs.</p>
                  </div>
                } 
              />
              
              <FAQItem 
                question="Do I need to credit NoLicense Nearby in my content?" 
                answer={
                  <p>
                    While not required, we appreciate credits when possible. You can credit us as "Music by NoLicense Nearby" 
                    in your description or credits section.
                  </p>
                } 
              />
              
              <FAQItem 
                question="Can I use the music on multiple platforms?" 
                answer={
                  <p>
                    The Basic Creator License is restricted to a single platform. Professional and Enterprise licenses allow usage 
                    across multiple platforms. If you need to expand your usage rights, you can purchase add-ons or upgrade your license.
                  </p>
                } 
              />
              
              <FAQItem 
                question="How do volume discounts work?" 
                answer={
                  <div>
                    <p className="mb-4">We offer significant savings when you license multiple tracks:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>5-track bundle:</strong> Save up to 20% off individual track pricing</li>
                      <li><strong>10-track bundle:</strong> Save up to 30% off individual track pricing</li>
                    </ul>
                    <p className="mt-4">Exact savings depend on your license tier. For custom bundles with more than 10 tracks, please contact us for special pricing.</p>
                  </div>
                } 
              />
              
              <FAQItem 
                question="What file formats do I receive?" 
                answer={
                  <p>
                    All licensed tracks are provided in high-quality MP3 (320 kbps) and WAV (44.1kHz/16-bit) formats.
                    If you need other formats, please specify in your custom license request.
                  </p>
                } 
              />
              
              <FAQItem 
                question="Do you offer refunds?" 
                answer={
                  <p>
                    Due to the digital nature of our products, we generally do not offer refunds. However, if there's a technical 
                    issue with your downloaded files, please contact us within 7 days of purchase, and we'll resolve the issue.
                  </p>
                } 
              />
              
              <FAQItem 
                question="How long is my license valid?" 
                answer={
                  <div>
                    <p className="mb-4">License duration varies by tier:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Basic Creator License:</strong> 12-month license duration</li>
                      <li><strong>Professional License:</strong> Perpetual license (never expires)</li>
                      <li><strong>Enterprise License:</strong> Perpetual license (never expires)</li>
                    </ul>
                  </div>
                } 
              />
              
              <FAQItem 
                question="Will I get copyright claims on YouTube or other platforms?" 
                answer={
                  <p>
                    No, you will not receive copyright claims for properly licensed music from NoLicense Nearby. We do not register our tracks 
                    with Content ID systems, ensuring you can use the music without copyright issues. If you ever receive a claim, contact us 
                    immediately, and we'll help resolve it.
                  </p>
                } 
              />
              
              <FAQItem 
                question="Can I edit or modify the tracks?" 
                answer={
                  <p>
                    Yes, all licenses allow you to edit the tracks to fit your content (trimming, looping, etc.). For more substantial 
                    modifications, you can purchase our Custom Edit Service add-on, where we'll professionally edit the track to your specifications.
                  </p>
                } 
              />
            </div>
            
            <div className="mt-12 p-6 bg-primary/10 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Still have questions?</h3>
              <p className="mb-4">
                If you need specific information about licensing or have a unique situation, please request a custom license through our licensing form.
              </p>
              <Link href="/license">
                <Button className="bg-primary hover:bg-primary/90 text-black">
                  Request Custom License
                </Button>
              </Link>
            </div>
            
            <p className="mt-8 text-sm text-muted-foreground">
              Last Updated: April 4, 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 